import {
    useCallback,
    forwardRef,
    type MouseEvent,
    type ComponentType,
    type Ref,
    type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import type { AnyRecord } from '@sso/shared/types';

export type NavigateButtonProps = {
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    type?: 'button' | 'submit' | 'reset';
    children?: ReactNode;
    state?: AnyRecord;
    replace?: boolean;
    to?: string;
    target?: string;
    href?: string;
};

function withNavigate<ButtonProps>(Button: ComponentType<ButtonProps>) {
    function NavigateButton(
        {
            onClick: _onClick,
            type = 'button',
            children,
            replace,
            state,
            to,
            target,
            href,

            ...props
        }: NavigateButtonProps & ButtonProps,
        ref: Ref<HTMLButtonElement>,
    ) {
        const navigate = useNavigate();

        const onClick = useCallback(
            (e: MouseEvent<HTMLButtonElement>) => {
                if (to) navigate(to, { replace, state });
                if (href) window.open(href, target);

                _onClick?.(e);
            },
            [navigate, to, state, replace, href, target, _onClick],
        );

        return (
            <Button {...(props as ButtonProps)} ref={ref} onClick={onClick} type={type}>
                {children && <span>{children}</span>}
            </Button>
        );
    }

    const displayName = Button.displayName || Button.name || 'Button';
    NavigateButton.displayName = `withNavigate(${displayName})`;

    return forwardRef(NavigateButton);
}

export default withNavigate;
